<template>
    <form class="notification-rule-form" @submit.prevent="submit">
        <div class="form-heading">
            <div>
                <h4>{{ formTitle }}</h4>
                <p>{{ __('notification_management.rule_form_subtitle') }}</p>
            </div>
            <golia-button variant="btn-fill gray" icon="fa-fw fa-arrow-left" :label="__('back_to_results')"
                type="button" @click="requestCancel" />
        </div>

        <section class="form-section">
            <div class="section-heading">
                <span>1</span>
                <div>
                    <h5>{{ __('notification_management.rule_details') }}</h5>
                    <p>{{ __('notification_management.rule_details_hint') }}</p>
                </div>
            </div>

            <div class="form-grid three-columns">
                <div class="form-field">
                    <label for="notification-rule-name">
                        {{ __('notification_management.rule_name') }}
                    </label>
                    <input id="notification-rule-name" v-model.trim="form.ruleName" class="form-control rule-control"
                        :class="{ 'is-invalid': errors.ruleName }" type="text"
                        :placeholder="__('notification_management.rule_name_placeholder')" />
                    <small v-if="errors.ruleName" class="field-error">{{ errors.ruleName }}</small>
                </div>

                <div class="form-field">
                    <label for="notification-rule-event">
                        {{ __('notification_management.monitored_event') }}
                    </label>
                    <select id="notification-rule-event" v-model="form.eventType" class="form-select rule-control"
                        :class="{ 'is-invalid': errors.eventType }">
                        <option :value="null" disabled>{{ __('select') }}</option>
                        <optgroup v-for="category in eventCategories" :key="category.key" :label="category.label">
                            <option v-for="event in category.events" :key="event.value" :value="event.value"
                                :disabled="event.disabled">
                                {{ event.label }}
                            </option>
                        </optgroup>
                    </select>
                    <small v-if="errors.eventType" class="field-error">{{ errors.eventType }}</small>
                </div>

                <div class="form-field">
                    <label for="notification-rule-language">
                        {{ __('notification_management.rule_language') }}
                    </label>
                    <select id="notification-rule-language" v-model="form.language" class="form-select rule-control"
                        :class="{ 'is-invalid': errors.language }">
                        <option v-for="option in languageOptions" :key="option.value" :value="option.value">
                            {{ option.label }}
                        </option>
                    </select>
                    <small v-if="errors.language" class="field-error">{{ errors.language }}</small>
                </div>
            </div>
        </section>

        <section class="form-section">
            <div class="section-heading">
                <span>2</span>
                <div>
                    <h5>{{ __('notification_management.target_vehicles') }}</h5>
                    <p>{{ __('notification_management.target_vehicles_hint') }}</p>
                </div>
            </div>

            <div class="scope-options" role="radiogroup">
                <label class="scope-option" :class="{ active: form.target.scope === 'fleet' }">
                    <input v-model="form.target.scope" type="radio" value="fleet" />
                    <i class="fa-solid fa-trucks"></i>
                    <span>
                        <strong>{{ __('notification_management.whole_fleet') }}</strong>
                        <small>{{ __('notification_management.whole_fleet_hint') }}</small>
                    </span>
                </label>
                <label class="scope-option" :class="{ active: form.target.scope === 'specific' }">
                    <input v-model="form.target.scope" type="radio" value="specific" />
                    <i class="fa-solid fa-truck"></i>
                    <span>
                        <strong>{{ __('notification_management.specific_vehicles') }}</strong>
                        <small>{{ __('notification_management.specific_vehicles_hint') }}</small>
                    </span>
                </label>
            </div>

            <div v-if="form.target.scope === 'specific'" class="specific-targets">
                <div class="form-field">
                    <label>{{ __('notification_management.vehicle_tags') }}</label>
                    <div v-if="companyTags.length" class="tag-options">
                        <button v-for="tag in companyTags" :key="tag.id" type="button" class="tag-option"
                            :class="{ selected: form.target.tagIds.includes(tag.id) }"
                            :aria-pressed="form.target.tagIds.includes(tag.id)" @click="toggleTag(tag.id)">
                            <i class="fa-solid fa-tag"></i>
                            {{ tag.name }}
                        </button>
                    </div>
                    <small v-else class="field-hint">
                        {{ __('notification_management.no_vehicle_tags') }}
                    </small>
                </div>

                <div class="form-field">
                    <label>{{ __('notification_management.vehicle_plates') }}</label>
                    <v-select v-model="form.target.vehicleIds" class="vehicle-select" multiple :options="vehicleOptions"
                        :reduce="(vehicle) => vehicle.value"
                        :placeholder="__('notification_management.select_vehicles')" :close-on-select="false" />
                </div>

                <div class="and-hint">
                    <i class="fa-solid fa-circle-info"></i>
                    {{ __('notification_management.target_and_hint') }}
                </div>
                <small v-if="errors.target" class="field-error">{{ errors.target }}</small>
            </div>
        </section>

        <section class="form-section">
            <div class="section-heading">
                <span>3</span>
                <div>
                    <h5>{{ __('notification_management.event_parameters') }}</h5>
                    <p>{{ __('notification_management.event_parameters_hint') }}</p>
                </div>
            </div>

            <notification-event-parameters ref="eventParameters" :event-type="form.eventType"
                :parameters="form.parameters" :pois="pois" :poi-categories="poiCategories"
                @input="form.parameters = $event" />
        </section>

        <section class="form-section">
            <div class="section-heading">
                <span>4</span>
                <div>
                    <h5>{{ __('notification_management.channels_recipients') }}</h5>
                    <p>{{ __('notification_management.channels_recipients_hint') }}</p>
                </div>
            </div>

            <div class="channel-configurations">
                <div class="channel-configuration" :class="{ enabled: form.channels.email.enabled }">
                    <div class="channel-heading">
                        <span class="channel-icon"><i class="fa-solid fa-envelope"></i></span>
                        <div>
                            <strong>{{ __('notification_management.email') }}</strong>
                            <small>{{ __('notification_management.email_recipients_hint') }}</small>
                        </div>
                        <button type="button" class="channel-toggle" :class="{ active: form.channels.email.enabled }"
                            role="switch" :aria-checked="form.channels.email.enabled"
                            @click="form.channels.email.enabled = !form.channels.email.enabled">
                            <span></span>
                        </button>
                    </div>
                    <notification-recipients-input v-if="form.channels.email.enabled"
                        :recipients="form.channels.email.recipients" type="email"
                        :placeholder="__('notification_management.add_email_recipient')"
                        @input="form.channels.email.recipients = $event" />
                    <small v-if="errors.email" class="field-error">{{ errors.email }}</small>
                </div>

                <!-- <div class="channel-configuration" :class="{ enabled: form.channels.sms.enabled }">
                    <div class="channel-heading">
                        <span class="channel-icon"><i class="fa-solid fa-message-sms"></i></span>
                        <div>
                            <strong>{{ __('notification_management.sms') }}</strong>
                            <small>{{ __('notification_management.sms_recipients_hint') }}</small>
                        </div>
                        <button type="button" class="channel-toggle" :class="{ active: form.channels.sms.enabled }"
                            role="switch" :aria-checked="form.channels.sms.enabled"
                            @click="form.channels.sms.enabled = !form.channels.sms.enabled">
                            <span></span>
                        </button>
                    </div>
                    <notification-recipients-input
                        v-if="form.channels.sms.enabled"
                        :recipients="form.channels.sms.recipients"
                        type="phone"
                        :placeholder="__('notification_management.add_sms_recipient')"
                        @input="form.channels.sms.recipients = $event"
                    />
                    <small v-if="errors.sms" class="field-error">{{ errors.sms }}</small>
                </div> -->
            </div>
            <small v-if="errors.channels" class="field-error">{{ errors.channels }}</small>
        </section>

        <div class="form-actions">
            <golia-button variant="btn-fill gray" :label="__('cancel')" type="button" @click="$emit('cancel')" />
            <golia-button variant="btn-fill dark-blue" icon="fa-fw fa-floppy-disk" :label="__('save')" type="submit"
                :loading="saving" :disabled="saving" />
        </div>
    </form>
</template>

<script>
    import NotificationEventParameters from './NotificationEventParameters.vue'
    import NotificationRecipientsInput from './NotificationRecipientsInput.vue'

    function createEmptyForm() {
        return {
            id: null,
            ruleName: '',
            eventType: null,
            language: 'it-IT',
            target: {
                scope: 'fleet',
                tagIds: [],
                vehicleIds: []
            },
            parameters: {},
            channels: {
                email: { enabled: false, recipients: [] },
                sms: { enabled: false, recipients: [] }
            }
        }
    }

    export default {
        name: 'NotificationRuleForm',
        components: {
            NotificationEventParameters,
            NotificationRecipientsInput
        },
        props: {
            rule: {
                type: Object,
                default: null
            },
            mode: {
                type: String,
                default: 'create',
                validator: (value) => ['create', 'edit', 'clone'].includes(value)
            },
            eventCategories: {
                type: Array,
                required: true
            },
            companyTags: {
                type: Array,
                default: () => []
            },
            vehicles: {
                type: Array,
                default: () => []
            },
            pois: {
                type: Array,
                default: () => []
            },
            poiCategories: {
                type: Array,
                default: () => []
            },
            saving: {
                type: Boolean,
                default: false
            }
        },
        data() {
            const form = this.createForm(this.rule)

            return {
                form,
                initialFormSnapshot: JSON.stringify(form),
                errors: {}
            }
        },
        computed: {
            hasUnsavedChanges() {
                return JSON.stringify(this.form) !== this.initialFormSnapshot
            },
            formTitle() {
                if (this.mode === 'clone') {
                    return this.__('notification_management.clone_rule')
                }
                if (this.mode === 'edit') {
                    return this.__('notification_management.edit_rule')
                }
                return this.__('notification_management.new_rule')
            },
            vehicleOptions() {
                return this.vehicles
                    .filter((vehicle) => vehicle?.id != null && vehicle?.plate)
                    .map((vehicle) => ({ value: vehicle.id, label: vehicle.plate }))
                    .sort((a, b) => a.label.localeCompare(b.label))
            },
            languageOptions() {
                return [
                    {
                        value: 'it-IT',
                        label: this.__('notification_management.language_italian')
                    },
                    {
                        value: 'en-EN',
                        label: this.__('notification_management.language_english')
                    }
                ]
            }
        },
        watch: {
            'form.eventType'(eventType, previousEventType) {
                if (eventType !== previousEventType) {
                    this.form.parameters = this.createDefaultParameters(eventType)
                }
            }
        },
        methods: {
            createDefaultParameters(eventType) {
                if (!eventType) return {}

                if (['poi_in', 'poi_out'].includes(eventType)) {
                    return {
                        poi_ids: [],
                        poi_categories: [],
                        cooldown_minutes: null
                    }
                }
                if (eventType === 'vehicle_stationary') {
                    return {
                        duration_hours: null,
                        exclude_pois: [],
                        exclude_poi_categories: [],
                        cooldown_hours: null,
                        notify_for_hours: null
                    }
                }
                if (
                    ['low_temperature', 'high_temperature', 'door_open', 'door_closed'].includes(
                        eventType
                    )
                ) {
                    return { cooldown_minutes: null }
                }

                return {}
            },
            createForm(rule) {
                const form = createEmptyForm()
                if (!rule) return form

                form.id = rule.id ?? null
                form.ruleName = rule.ruleName || ''
                form.eventType = rule.event?.type || null
                form.language = ['it-IT', 'en-EN'].includes(rule.language)
                    ? rule.language
                    : 'it-IT'
                form.target = {
                    scope: rule.target?.scope || (rule.application?.type === 'fleet' ? 'fleet' : 'specific'),
                    tagIds: [...(rule.target?.tagIds || [])],
                    vehicleIds: [...(rule.target?.vehicleIds || [])]
                }
                form.parameters = {
                    ...this.createDefaultParameters(form.eventType),
                    ...(rule.parameters || {})
                }
                form.channels = {
                    email: {
                        enabled: !!rule.channels?.email?.enabled,
                        recipients: [...(rule.channels?.email?.recipients || [])]
                    },
                    sms: {
                        enabled: !!rule.channels?.sms?.enabled,
                        recipients: [...(rule.channels?.sms?.recipients || [])]
                    }
                }

                return form
            },
            toggleTag(tagId) {
                const index = this.form.target.tagIds.indexOf(tagId)
                if (index >= 0) this.form.target.tagIds.splice(index, 1)
                else this.form.target.tagIds.push(tagId)
            },
            validate() {
                const errors = {}
                if (!this.form.ruleName) errors.ruleName = this.__('notification_management.required_field')
                if (!this.form.language) errors.language = this.__('notification_management.required_field')
                if (!this.form.eventType) errors.eventType = this.__('notification_management.required_field')
                else if (!this.isEventSupported(this.form.eventType)) {
                    errors.eventType = this.__('notification_management.event_not_supported_error')
                }

                if (
                    this.form.target.scope === 'specific' &&
                    this.form.target.tagIds.length === 0 &&
                    this.form.target.vehicleIds.length === 0
                ) {
                    errors.target = this.__('notification_management.target_required')
                }

                if (!this.form.channels.email.enabled && !this.form.channels.sms.enabled) {
                    errors.channels = this.__('notification_management.channel_required')
                }
                if (
                    this.form.channels.email.enabled &&
                    this.form.channels.email.recipients.length === 0
                ) {
                    errors.email = this.__('notification_management.recipient_required')
                }
                if (
                    this.form.channels.sms.enabled &&
                    this.form.channels.sms.recipients.length === 0
                ) {
                    errors.sms = this.__('notification_management.recipient_required')
                }

                this.errors = errors
                const parametersAreValid = this.$refs.eventParameters?.validate() !== false
                return Object.keys(errors).length === 0 && parametersAreValid
            },
            submit() {
                if (this.saving) return
                if (!this.validate()) return
                this.$emit('save', JSON.parse(JSON.stringify(this.form)))
            },
            requestCancel() {
                if (!this.hasUnsavedChanges) {
                    this.$emit('cancel')
                    return
                }

                this.$bvModal
                    .msgBoxConfirm(
                        this.__('notification_management.unsaved_changes_message'),
                        {
                            title: this.__('notification_management.unsaved_changes_title'),
                            size: 'sm',
                            buttonSize: 'sm',
                            okVariant: 'danger',
                            okTitle: this.__('notification_management.discard_changes'),
                            cancelTitle: this.__('notification_management.continue_editing'),
                            centered: true
                        }
                    )
                    .then((confirmed) => {
                        if (confirmed) this.$emit('cancel')
                    })
                    .catch(() => null)
            },
            isEventSupported(eventType) {
                return this.eventCategories.some((category) =>
                    category.events.some((event) => event.value === eventType && !event.disabled)
                )
            }
        }
    }
</script>

<style scoped lang="scss">
    .notification-rule-form {
        display: flex;
        flex-direction: column;
        gap: 16px;
        width: 100%;
        margin: 0 auto;
        padding-bottom: 24px;
        color: var(--main-text-color);
    }

    .form-heading,
    .section-heading,
    .channel-heading {
        display: flex;
        align-items: center;
    }

    .form-heading {
        justify-content: space-between;
        gap: 16px;

        h4 {
            margin: 0 0 4px;
            font-family: 'Stolzl-Medium';
            font-size: 20px;
        }

        p {
            margin: 0;
            color: #6b7785;
            font-size: 12px;
        }
    }

    .form-section {
        border: 1px solid #d2d2d2;
        border-radius: 14px;
        padding: 18px;
        background: var(--main-inner-bg-color);
    }

    .section-heading {
        gap: 10px;
        margin-bottom: 16px;

        >span {
            display: inline-flex;
            width: 30px;
            height: 30px;
            flex: 0 0 30px;
            align-items: center;
            justify-content: center;
            border-radius: 9px;
            background: var(--dark-blue);
            color: #fff;
            font-family: 'Stolzl-Medium';
            font-size: 12px;
        }

        h5 {
            margin: 0 0 2px;
            font-family: 'Stolzl-Medium';
            font-size: 14px;
        }

        p {
            margin: 0;
            color: #6b7785;
            font-size: 11px;
        }
    }

    .form-grid {
        display: grid;
        gap: 14px;

        &.two-columns {
            grid-template-columns: repeat(2, minmax(0, 1fr));
        }

        &.three-columns {
            grid-template-columns: repeat(3, minmax(0, 1fr));
        }
    }

    .form-field {
        min-width: 0;

        label {
            display: block;
            margin-bottom: 7px;
            font-family: 'Stolzl-Medium';
            font-size: 12px;
        }
    }

    .rule-control {
        min-height: 42px;
        border: 1px solid #d2d2d2;
        border-radius: 12px;
        padding: 8px 12px;
        background: #fff;
        color: #333;
        font-size: 14px;

        &:focus {
            border-color: #d2d2d2;
            background: var(--main-gray);
            box-shadow: none;
        }
    }

    .scope-options {
        display: grid;
        grid-template-columns: repeat(2, minmax(0, 1fr));
        gap: 12px;
    }

    .scope-option {
        position: relative;
        display: flex;
        align-items: center;
        gap: 12px;
        margin: 0;
        border: 1px solid #d2d2d2;
        border-radius: 12px;
        padding: 14px 48px 14px 14px;
        background: #fff;
        cursor: pointer;

        input[type='radio'] {
            position: absolute;
            width: 1px;
            height: 1px;
            margin: -1px;
            overflow: hidden;
            clip: rect(0 0 0 0);
            clip-path: inset(50%);
            white-space: nowrap;
        }

        &::after {
            position: absolute;
            top: 50%;
            right: 14px;
            display: flex;
            width: 24px;
            height: 24px;
            align-items: center;
            justify-content: center;
            border: 1px solid #c5ccd3;
            border-radius: 50%;
            color: transparent;
            content: '\2713';
            font-family: 'Stolzl-Medium';
            font-size: 13px;
        }

        &:hover {
            border-color: var(--dark-blue);
        }

        &:focus-within {
            box-shadow: 0 0 0 3px rgba(29, 95, 159, 0.18);
        }

        &.active {
            border-color: var(--dark-blue);
            background: var(--dark-blue);
            box-shadow: 0 5px 14px rgba(29, 95, 159, 0.22);
            color: #fff;

            &::after {
                border-color: #fff;
                background: #fff;
                color: var(--dark-blue);
            }

            >i,
            small {
                color: #fff;
            }

            small {
                opacity: 0.82;
            }
        }

        >i {
            color: var(--dark-blue);
            font-size: 20px;
        }

        >span {
            display: flex;
            flex-direction: column;
            gap: 2px;
        }

        strong {
            font-family: 'Stolzl-Medium';
            font-size: 12px;
        }

        small {
            color: #6b7785;
            font-size: 10px;
        }
    }

    .specific-targets {
        display: grid;
        gap: 16px;
        margin-top: 16px;
    }

    .tag-options {
        display: flex;
        flex-wrap: wrap;
        gap: 8px;
    }

    .tag-option {
        border: 1px solid #d2d2d2;
        border-radius: 999px;
        padding: 6px 10px;
        background: #fff;
        color: var(--main-text-color);
        font-size: 11px;

        &.selected {
            border-color: var(--dark-blue);
            background: var(--dark-blue);
            color: #fff;
        }
    }

    .vehicle-select {
        font-size: 13px;

        ::v-deep(.vs__dropdown-toggle) {
            min-height: 42px;
            border: 1px solid #d2d2d2;
            border-radius: 12px;
            background: #fff;
        }
    }

    .and-hint {
        display: flex;
        align-items: flex-start;
        gap: 8px;
        border-radius: 10px;
        padding: 10px 12px;
        background: rgba(29, 95, 159, 0.08);
        color: #1d5f9f;
        font-size: 11px;
    }

    .channel-configurations {
        display: grid;
        grid-template-columns: repeat(2, minmax(0, 1fr));
        gap: 12px;
    }

    .channel-configuration {
        border: 1px solid #d2d2d2;
        border-radius: 12px;
        padding: 14px;
        background: #fff;

        &.enabled {
            border-color: var(--success);
        }
    }

    .channel-heading {
        gap: 10px;

        >div {
            display: flex;
            flex: 1;
            flex-direction: column;
        }

        strong {
            font-family: 'Stolzl-Medium';
            font-size: 12px;
        }

        small {
            color: #6b7785;
            font-size: 10px;
        }
    }

    .channel-icon {
        display: inline-flex;
        width: 34px;
        height: 34px;
        align-items: center;
        justify-content: center;
        border-radius: 10px;
        background: var(--main-gray);
        color: var(--dark-blue);
    }

    .channel-toggle {
        width: 42px;
        height: 24px;
        border: 0;
        border-radius: 999px;
        padding: 3px;
        background: #bac2ca;
        transition: background 0.2s ease;

        span {
            display: block;
            width: 18px;
            height: 18px;
            border-radius: 50%;
            background: #fff;
            transition: transform 0.2s ease;
        }

        &.active {
            background: var(--success);

            span {
                transform: translateX(18px);
            }
        }
    }

    .field-error {
        display: block;
        margin-top: 6px;
        color: var(--danger);
        font-size: 10px;
    }

    .field-hint {
        color: #6b7785;
        font-size: 11px;
    }

    .form-actions {
        display: flex;
        justify-content: flex-end;
        gap: 10px;
    }

    @media (max-width: 760px) {

        .form-grid.two-columns,
        .form-grid.three-columns,
        .scope-options,
        .channel-configurations {
            grid-template-columns: 1fr;
        }

        .form-heading {
            align-items: flex-start;
            flex-direction: column;
        }
    }
</style>
